// ============================================================================
// NoticeGenerateModal — generates a merged notice for printing
// ============================================================================
// Calls /api/v1/NoticeTemplate/preview with the case + recipient + form
// overrides (notice_no, notice_date, demand_amount). Renders the returned
// HTML inside a sandboxed iframe and exposes a Print button that triggers
// window.print() on the iframe contents (browser print dialog opens with
// just the notice — modal chrome is excluded).
//
// Recipient picker iterates through the case's parties (Applicant /
// Co-Applicant / Guarantor). Switching recipients re-fetches the merged
// HTML — recipient_name / address change but case-level data stays.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { noticeTemplateService } from 'api/services/noticeTemplateService';
import { litigationCaseService } from 'api/services/litigationCaseService';
import type { CaseParty } from 'api/types/litigationCase.types';
import type { NoticePreviewResponse } from 'api/types/noticeTemplate.types';

export interface NoticeGenerateModalProps {
  isOpen: boolean;
  caseId: string;
  caseCode: string;
  /** Template code to resolve and render. The modal looks up the templateId
   *  via /code/{code} on open. Demo uses 'ESAF-SMA-1-2'. */
  templateCode: string;
  /** Form-side values typed by the user that may not yet be persisted.
   *  Passed as Overrides to the backend so the preview reflects the latest
   *  input (notice_no, notice_date, demand_amount, etc.). */
  overrides?: Record<string, string | null>;
  onClose: () => void;
}

const PARTY_ROLE_LABEL: Record<string, string> = {
  PRIMARY: 'Applicant',
  CO_BORROWER: 'Co-Applicant',
  GUARANTOR: 'Guarantor',
};

const NoticeGenerateModal: React.FC<NoticeGenerateModalProps> = ({
  isOpen, caseId, caseCode, templateCode, overrides, onClose,
}) => {
  const [parties, setParties] = useState<CaseParty[]>([]);
  const [selectedPartyId, setSelectedPartyId] = useState<string>('');
  const [templateId, setTemplateId] = useState<string>('');
  const [preview, setPreview] = useState<NoticePreviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Load parties + resolve template id when modal opens
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const [list, tmplResp] = await Promise.all([
          litigationCaseService.getParties(caseId),
          noticeTemplateService.getByCode(templateCode),
        ]);
        if (cancelled) return;
        const tmpl = tmplResp?.data;
        setParties(list);
        setSelectedPartyId(list[0]?.partyId ?? '');
        setTemplateId(tmpl?.id ?? '');
        if (!tmpl) setError(`Template '${templateCode}' not found.`);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load recipients/template.');
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, caseId, templateCode]);

  // Re-fetch preview whenever selectedPartyId / templateId / overrides change
  useEffect(() => {
    if (!isOpen || !templateId || !selectedPartyId) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const r = await noticeTemplateService.preview({
          templateId,
          caseId,
          partyId: selectedPartyId,
          overrides: overrides ?? {},
        });
        if (!cancelled) setPreview(r);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to render notice.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, templateId, caseId, selectedPartyId, JSON.stringify(overrides ?? {})]);

  const recipientOptions = useMemo(
    () => parties.map((p) => ({
      value: p.partyId,
      label: `${p.customerName} — ${PARTY_ROLE_LABEL[p.partyRole] ?? p.partyRole}`,
    })),
    [parties],
  );

  const srcDoc = useMemo(() => {
    if (!preview) return '';
    return `<!doctype html>
<html><head><meta charset="utf-8" />
<style>
  @page { margin: 18mm; }
  body {
    font-family: 'Times New Roman', Georgia, serif;
    font-size: 12pt;
    line-height: 1.55;
    color: #000;
    background: #fff;
    padding: 18mm 22mm;
    margin: 0;
    max-width: 210mm;
  }
  p { margin: 0 0 10pt 0; }
  b, strong { font-weight: 700; }
  @media print { body { padding: 0; } }
</style></head>
<body>${preview.bodyHtml}</body></html>`;
  }, [preview]);

  const handlePrint = useCallback(() => {
    const win = iframeRef.current?.contentWindow;
    if (!win) {
      notificationService.toast.error('Cannot access print frame.');
      return;
    }
    try {
      win.focus();
      win.print();
    } catch {
      notificationService.toast.error('Print failed. Use Ctrl+P inside the preview.');
    }
  }, []);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            <IconSystem.mail size={18} style={{ marginRight: '0.4rem', verticalAlign: '-3px' }} />
            Generate Notice — {caseCode}
          </h2>
          <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
        </div>

        <div style={{
          padding: '0.6rem 1rem',
          borderBottom: '1px solid var(--border-color)',
          background: 'var(--bg-secondary)',
          display: 'flex', gap: '0.75rem', alignItems: 'flex-end',
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <SyutSelect
              label="Recipient"
              options={recipientOptions}
              value={selectedPartyId}
              onChange={(v) => setSelectedPartyId((v as string) ?? '')}
              size="sm"
              isClearable={false}
              placeholder={recipientOptions.length === 0 ? 'No recipients available' : 'Select recipient'}
            />
            {preview?.templateCode && (
              <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: '0.2rem' }}>
                Template: <strong>{preview.templateCode}</strong>
                {preview.recipientLabel ? ` · ${preview.recipientLabel}` : ''}
              </div>
            )}
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handlePrint}
            disabled={!preview || loading}
          >
            <IconSystem.file size={14} style={{ marginRight: '0.4rem' }} />
            Print
          </button>
        </div>

        <div className="modal-content" style={{ padding: 0, flex: 1, minHeight: 400, display: 'flex' }}>
          {loading && (
            <div style={{ margin: 'auto', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
              Rendering notice…
            </div>
          )}
          {!loading && error && (
            <div style={{ margin: 'auto', color: '#dc2626', fontSize: '0.85rem', textAlign: 'center', padding: '2rem' }}>
              {error}
            </div>
          )}
          {!loading && !error && preview && (
            <iframe
              ref={iframeRef}
              title="Notice Preview"
              srcDoc={srcDoc}
              sandbox="allow-same-origin allow-modals"
              style={{
                flex: 1, width: '100%', border: 'none',
                background: 'white', minHeight: 400,
              }}
            />
          )}
          {!loading && !error && !preview && !templateId && (
            <div style={{ margin: 'auto', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
              Resolving template…
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </>
  );
};

export default NoticeGenerateModal;
